import { useState } from 'react'
import { LogOut, X, Moon, Sun, Users, Bell } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TabBar, type Section } from './TabBar'
import { ChatPanel } from './ChatPanel'
import { CanvasPanel } from './CanvasPanel'
import { IntegrationsPage } from '../pages/IntegrationsPage'
import { RecordPage } from '../pages/RecordPage'
import { KnowledgePage } from '../pages/KnowledgePage'
import { companionById } from '../data/companions'
import { CompanionAvatar } from '@/components/CompanionAvatar'
import type { User } from '@/api/auth'

export function Shell({
  user, coach, dark, onToggleDark, onChangeCoach, onSignOut,
}: {
  user: User
  coach: string
  dark: boolean
  onToggleDark: () => void
  onChangeCoach: () => void
  onSignOut: () => void
}) {
  const [section, setSection] = useState<Section>('record')
  const [canvasOpen, setCanvasOpen] = useState(false)
  const [account, setAccount] = useState(false)
  const [policy, setPolicy] = useState<'privacy' | 'terms' | null>(null)
  const [chatPrompt, setChatPrompt] = useState<string | undefined>()
  const [chatNonce, setChatNonce] = useState(0)

  const askSara = (p: string) => {
    setChatPrompt(p)
    setChatNonce((n) => n + 1)
    setCanvasOpen(false)
    setSection('chat')
  }

  const initial = (user.name || user.email || 'U').trim()[0].toUpperCase()
  const accountBtn = (
    <button onClick={() => setAccount(true)} aria-label="Account"
      className="flex size-8 items-center justify-center rounded-full bg-accent text-accent-foreground text-sm font-semibold">
      {initial}
    </button>
  )

  return (
    <div className="relative flex h-full flex-col">
      <div className="relative min-h-0 flex-1">
        {section === 'record' && (
          <RecordPage onMenu={() => {}} showMenu={false} onAskSara={askSara} right={accountBtn} userName={user.name} userId={user.id} />
        )}
        {section === 'chat' && (
          <ChatPanel
            key={chatNonce}
            coach={coach}
            userName={user.name}
            initialPrompt={chatPrompt}
            onMenu={() => {}}
            showMenu={false}
            onToggleCanvas={() => setCanvasOpen((v) => !v)}
            canvasOpen={canvasOpen}
          />
        )}
        {section === 'integrations' && <IntegrationsPage onMenu={() => {}} showMenu={false} userId={user.id} />}
        {section === 'knowledge' && <KnowledgePage onMenu={() => {}} showMenu={false} />}

        {canvasOpen && section === 'chat' && (
          <div className="absolute inset-0 z-40 animate-fade-up">
            <CanvasPanel onClose={() => setCanvasOpen(false)} />
          </div>
        )}

        {account && (
          <AccountSheet
            user={user}
            coach={coach}
            onClose={() => setAccount(false)}
            onSignOut={onSignOut}
            onPolicy={setPolicy}
            dark={dark}
            onToggleDark={onToggleDark}
            onChangeCoach={() => { setAccount(false); onChangeCoach() }}
          />
        )}
        {policy && <PolicyModal kind={policy} onClose={() => setPolicy(null)} />}

        {/* Proactive in-character check-in ("she texts you first") — suppressed until the native
            app ships, where it belongs as a real push notification (component kept for reuse). */}
        {/* <NudgeToast coach={coach} onOpen={() => { setCanvasOpen(false); setSection('chat') }} /> */}
      </div>

      <TabBar active={section} onNavigate={(s) => { setCanvasOpen(false); setSection(s) }} />
    </div>
  )
}

function AccountSheet({
  user, coach, onClose, onSignOut, onPolicy, dark, onToggleDark, onChangeCoach,
}: {
  user: User
  coach: string
  onClose: () => void
  onSignOut: () => void
  onPolicy: (k: 'privacy' | 'terms') => void
  dark: boolean
  onToggleDark: () => void
  onChangeCoach: () => void
}) {
  const c = companionById(coach)
  return (
    <div className="absolute inset-0 z-50 flex flex-col justify-end bg-overlay" onClick={onClose}>
      <div
        className="rounded-t-3xl bg-card p-5 animate-fade-up"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-border" />
        <div className="flex items-center gap-3">
          <div className="flex size-11 items-center justify-center rounded-full bg-accent text-accent-foreground font-semibold">
            {(user.name || user.email || 'U').trim()[0].toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-foreground">{user.name || 'You'}</div>
            <div className="truncate text-xs text-muted-foreground">{user.email}</div>
          </div>
        </div>

        {/* Current coach + change */}
        <button onClick={onChangeCoach} className="mt-4 flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 text-left hover:bg-muted">
          {c && <CompanionAvatar c={c} size={36} />}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-foreground">Your coach: {c?.name}</div>
            <div className="text-[11px] text-muted-foreground">Tap to switch coach</div>
          </div>
          <Users className="size-4 text-muted-foreground" />
        </button>

        <button onClick={onToggleDark} className="mt-2 flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm font-medium text-foreground hover:bg-muted">
          {dark ? <Moon className="size-4" /> : <Sun className="size-4" />}
          <span className="flex-1 text-left">Dark mode</span>
          <span className={cn('relative h-6 w-10 rounded-full transition-colors', dark ? 'bg-primary' : 'bg-border')}>
            <span className={cn('absolute top-0.5 size-5 rounded-full bg-card shadow transition-all', dark ? 'left-[18px]' : 'left-0.5')} />
          </span>
        </button>
        <button
          onClick={() => {
            try {
              if ('Notification' in window) Notification.requestPermission().then((p) => {
                if (p === 'granted') new Notification('SARA', { body: `${c?.name ?? 'Your coach'} will check in on you here.` })
              })
            } catch { /* ignore */ }
          }}
          className="mt-2 flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm font-medium text-foreground hover:bg-muted"
        >
          <Bell className="size-4" /> Turn on nudges
        </button>
        <button onClick={onSignOut} className="mt-2 flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm font-medium text-foreground hover:bg-muted">
          <LogOut className="size-4" /> Sign out
        </button>
        <div className="mt-4 flex items-center gap-2 text-[11px] text-muted-foreground">
          <button onClick={() => onPolicy('privacy')} className="hover:text-foreground hover:underline">Privacy</button>
          <span>·</span>
          <button onClick={() => onPolicy('terms')} className="hover:text-foreground hover:underline">Terms</button>
          <span className="ml-auto">© 2026 Innovaccer Inc.</span>
        </div>
      </div>
    </div>
  )
}

function PolicyModal({ kind, onClose }: { kind: 'privacy' | 'terms'; onClose: () => void }) {
  const isPrivacy = kind === 'privacy'
  return (
    <div className="absolute inset-0 z-[60] flex items-center justify-center bg-overlay p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-card p-5 shadow-lg animate-scale-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h2 className="text-base font-semibold text-foreground">{isPrivacy ? 'Privacy Policy' : 'Terms of Service'}</h2>
          <button onClick={onClose} className="-mr-1.5 rounded-md p-1.5 hover:bg-muted" aria-label="Close"><X className="size-5 text-foreground" /></button>
        </div>
        <div className="mt-3 space-y-2 text-xs leading-relaxed text-muted-foreground">
          {isPrivacy ? (
            <>
              <p>Your health data is yours. SARA accesses connected records and devices only with your explicit consent, uses them solely to provide your coaching, and never sells your data.</p>
              <p>Conversations with your coach are treated as sensitive health data — encrypted in transit and at rest. You can disconnect any source or delete your data at any time. In production, SARA operates under HIPAA-aligned safeguards and BAAs with covered partners.</p>
              <p className="text-[10px]">Your coach is an AI companion, not a person. This is a demonstration build; data shown is illustrative.</p>
            </>
          ) : (
            <>
              <p>SARA provides personalized health coaching, education, and care navigation through an AI companion. She is not a medical service and does not provide diagnosis, treatment, or clinical advice.</p>
              <p>Always consult a qualified healthcare provider for medical decisions. In an emergency, call your local emergency number.</p>
              <p className="text-[10px]">© 2026 Innovaccer Inc. SARA and the SARA mark are trademarks of Innovaccer Inc.</p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
